import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '@/api/envelope';
import { portalLogin, portalSendCode, portalVerify } from '@/api/generated';
import { tokenStore } from '@/api/token-store';
import { landingFor } from '@/app/navigation';
import { ROUTES } from '@/app/routes';
import { ChooseOrganizationForm } from '@/auth/ChooseOrganizationForm';
import { toCurrentUser } from '@/auth/profile';
import { signOutNotice } from '@/auth/signOutNotice';
import { useAuth } from '@/auth/useAuth';

import { AuthLayout } from './AuthLayout';
import { CredentialsForm } from './CredentialsForm';
import { signInFailure } from './signInFailure';
import { VerificationForm } from './VerificationForm';
import type { CurrentUser, Membership } from '@/auth/auth-context';
import { stateFlag, stateText } from '@/lib/routerState';

import type { CredentialsValues, VerificationValues } from './schemas';

/**
 * The backend's answer when the proof of the password step has run out
 * (portal-session-rules, B7). Matched here rather than in signInFailure.ts,
 * which another change edits (design Risks, D21).
 */
const SIGN_IN_TIMED_OUT = 'Your sign-in timed out. Enter your email and password again.';

function timedOut(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 401 &&
    error.apiError === SIGN_IN_TIMED_OUT
  );
}

/**
 * Signing in takes three requests, not one: check the password, ask for a code
 * to be emailed, then exchange the code for tokens.
 *
 * One screen for all three roles. Activist Technologies staff, an
 * organization's Super Admin and an organization's Admin are the same kind of
 * account on the backend and sign in at the same endpoints; the only thing that
 * differs is the role the reply names, and whether it names an organization.
 * That is what decides where the person lands.
 *
 * The two steps live on one route rather than two, because the user id from the
 * first step is needed by the other two and the address bar is the wrong place
 * to carry it. Refreshing during the code step returns to the start, which is
 * correct - the code belongs to an attempt that no longer exists.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signIn, signOut } = useAuth();

  /*
   * Chose Log Out: the session ends here, on arrival, not before the
   * navigation. Ending it earlier would tear the portal down under a dirty
   * form's "Leave without saving?" - and Stay has to leave the person signed
   * in, on their page, with their edits.
   */
  const loggingOut = stateFlag(location.state, 'loggingOut');

  /*
   * The marker is spent as soon as it is read. Left on the history entry it
   * would still be there when the person signs in again on this same page,
   * and would sign them straight back out - sending them round the whole
   * sign-in, code and all, a second time.
   */
  useEffect(() => {
    if (!loggingOut) return;
    if (user) signOut();
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [loggingOut, user, signOut, navigate, location.pathname, location.search]);

  /*
   * The password step hands back the person's id and a short-lived login
   * token. Both code requests carry the token: without it the backend will
   * not email a code or exchange one, so an id alone - which every colleague
   * can see in the Users list - buys nothing.
   */
  const [attempt, setAttempt] = useState<{ id: string; loginToken: string } | null>(null);
  // Set when somebody belongs to more than one organization and has to say
  // which they are opening. Everything needed to finish is held here, so the
  // choice costs no extra request.
  const [choice, setChoice] = useState<{
    memberships: Membership[];
    finish: (chosen: Membership) => void;
  } | null>(null);
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  // Where the guard turned them away from, if that is how they got here.
  const wanted = stateText(location.state, 'from') ?? null;

  /*
   * Why the last session in this tab ended, if the server ended it, and whose
   * it was (B15, I6, I7). Read as the page first draws. The sentence is said
   * once - cleared once it is on screen, so a reload does not say it again -
   * but whose session it was is kept until somebody signs in here.
   */
  const [ended] = useState(() => signOutNotice.read());
  useEffect(() => {
    signOutNotice.clearSentence();
  }, []);

  /*
   * Where a person goes once signed in. The page they were on only if it was
   * theirs: the saved address can hold the previous person's search words
   * and filters (B18, I7). Everyone else goes to their own landing page.
   */
  const destinationFor = (person: CurrentUser) =>
    wanted && (!ended?.userId || ended.userId === person.id)
      ? wanted
      : landingFor(person.role);

  /** Back to the password step, keeping the address (B16). */
  const startAgain = () => {
    setAttempt(null);
    setFormError(null);
    setResent(false);
  };

  // Carried from the invitation page after somebody sets their password, so
  // they are told it worked and do not have to retype the address they just
  // proved they own.
  const arrived = {
    message: stateText(location.state, 'message'),
    email: stateText(location.state, 'email'),
  };

  const credentials = useMutation({
    mutationFn: async (values: CredentialsValues) => {
      setEmail(values.email);
      // The portal endpoint issues and emails the code itself, so unlike the
      // app's flow there is no second call to make here.
      const reply = await portalLogin({ body: values, throwOnError: true });
      const data = unwrap(reply.data);
      if (!data.portal_user_id || !data.login_token) {
        throw new Error('The server did not say who signed in.');
      }
      return { id: data.portal_user_id, loginToken: data.login_token };
    },
    onMutate: () => setFormError(null),
    onSuccess: (started) => setAttempt(started),
    // Never says which of the two was wrong: that tells an attacker which
    // email addresses are registered. A rate limit or an outage says so.
    onError: (error) => setFormError(signInFailure(error, 'password')),
  });

  const verify = useMutation({
    mutationFn: async (values: VerificationValues) => {
      if (!attempt) throw new Error('No sign-in attempt is in progress.');
      const reply = await portalVerify({
        body: {
          portal_user_id: attempt.id,
          login_token: attempt.loginToken,
          code: values.code,
        },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFormError(null),
    onSuccess: (data) => {
      if (!data?.access_token || !data.refresh_token) {
        setFormError('Signing in did not complete. Please try again.');
        return;
      }

      // Every organization they belong to, with the role held in each, read
      // the same way /me and every renewal are (design Decision 7). An
      // unrecognised role stops the sign-in rather than landing somebody on a
      // page by guesswork - and nothing is stored, so nobody is half signed in.
      const outcome = toCurrentUser(data.user, null);
      if (outcome.kind === 'unknownRole') {
        setFormError('This account has a role the portal does not recognise.');
        return;
      }

      const finish = (next: CurrentUser) => {
        tokenStore.set({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
        signIn(next);
        // Staff have no dashboard, so sending everybody to one would greet a
        // staff member with a refusal the moment they signed in.
        void navigate(destinationFor(next), { replace: true });
      };

      // Staff belong to nowhere and go straight to the staff area.
      if (outcome.kind === 'user') return finish(outcome.user);

      // Nobody else has an organization open yet. One needs no question.
      // Several do - and asking here rather than dropping them somewhere
      // arbitrary is the whole point, because their role may differ.
      const { remaining, userIn } = outcome;
      if (remaining.length === 0) {
        setFormError('This account does not belong to any organization yet.');
        return;
      }
      if (remaining.length === 1) return finish(userIn(remaining[0]));
      setChoice({ memberships: remaining, finish: (chosen) => finish(userIn(chosen)) });
    },
    onError: (error) => {
      // The sign-in ran out on the code step: back to the password step,
      // with the address kept and the reason said (B17, D17).
      if (timedOut(error)) {
        startAgain();
        setFormError(SIGN_IN_TIMED_OUT);
        return;
      }
      setFormError(signInFailure(error, 'code'));
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      if (!attempt) return;
      await portalSendCode({
        body: { portal_user_id: attempt.id, login_token: attempt.loginToken },
        throwOnError: true,
      });
    },
    onMutate: () => {
      setFormError(null);
      setResent(false);
    },
    // A new code really was issued, but nothing on screen said so, so people
    // pressed it again, or kept typing the code that had just been retired.
    onSuccess: () => setResent(true),
    onError: (error) => {
      if (timedOut(error)) {
        startAgain();
        setFormError(SIGN_IN_TIMED_OUT);
        return;
      }
      setFormError(signInFailure(error, 'resend'));
    },
  });

  // A new tab asks the browser's other tabs for their sign-in first. Showing
  // the form meanwhile would invite somebody already signed in to sign in
  // again - which, with one session per person, signs the other tab out
  // (portal-session-rules, B8, D8).
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading" data-testid="sign-in-loading" />
      </Box>
    );
  }

  // Somebody already signed in has no business on this screen - unless they
  // are here to be signed out, which the effect above is doing right now.
  if (user) {
    if (loggingOut) return null;
    // The same destination the sign-in itself chose: this redirect runs as
    // soon as the person is signed in, and must not overrule it.
    return <Navigate to={destinationFor(user)} replace />;
  }

  if (choice) {
    return (
      <AuthLayout
        title="Choose an organization"
        description="You work with more than one. You can switch at any time."
        error={formError}
        testId="sign-in-choose-org"
      >
        <ChooseOrganizationForm
          testId="sign-in-choose-org"
          memberships={choice.memberships}
          onChoose={choice.finish}
        />
      </AuthLayout>
    );
  }

  if (attempt) {
    return (
      <AuthLayout
        title="Check your email"
        description={`We sent a code to ${email}.`}
        error={formError}
        testId="sign-in-code"
      >
        <VerificationForm
          onSubmit={(values) => verify.mutate(values)}
          onResend={() => resend.mutate()}
          onStartAgain={startAgain}
          pending={verify.isPending}
          resending={resend.isPending}
          resent={resent}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      description="Manage your organization's presence in ActivistARC."
      error={formError}
      testId="sign-in"
      footer={
        <Link
          component={RouterLink}
          to={ROUTES.forgotPassword}
          variant="body2"
          data-testid="sign-in-forgot-password-link"
        >
          Forgotten your password?
        </Link>
      }
    >
      {ended?.sentence ? (
        <Alert
          severity="info"
          role="status"
          data-testid="sign-in-ended-message"
          sx={{ mb: 2 }}
        >
          {ended.sentence}
        </Alert>
      ) : null}

      {arrived.message ? (
        <Alert
          severity="success"
          role="status"
          data-testid="sign-in-arrived-message"
          sx={{ mb: 2 }}
        >
          {arrived.message}
        </Alert>
      ) : null}

      <CredentialsForm
        onSubmit={(values) => credentials.mutate(values)}
        pending={credentials.isPending}
        // The address they gave, when they come back from the code step.
        defaultEmail={arrived.email ?? (email || undefined)}
      />
    </AuthLayout>
  );
}
